// ConfigPage replaces upstream's Admin > Configuration accordions: sections in
// a side list with their on/off state, each section's settings with plain
// labels and the right input for their type, search across every setting, and
// one save bar that shows all changes for review before they are applied.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CombinedError, gql, useMutation, useQuery } from 'urql'
import { startCase } from 'lodash'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  CleaningServices,
  Close,
  ContentCopy,
  Dns,
  Email,
  Feedback,
  GitHub,
  Lock,
  Login,
  MarkEmailRead,
  Settings,
  Sms,
  Tune,
  Undo,
  Visibility,
  VisibilityOff,
  Webhook,
  Forum,
} from '@mui/icons-material'
import { GenericError } from '../error-pages'
import { fieldErrors, nonFieldErrors } from '../util/errutil'
import { useURLParam } from '../actions'
import SlackActions from '../admin/SlackActions'
import { Page, Panel, SearchBox, useBrand } from './ui'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }

const configQuery = gql`
  query ErecordsConfig {
    config(all: true) {
      id
      description
      password
      type
      value
      deprecated
    }
    configHints {
      id
      value
    }
  }
`

const setConfig = gql`
  mutation ErecordsSetConfig($input: [ConfigValueInput!]) {
    setConfig(input: $input)
  }
`

type ConfigValue = {
  id: string
  description: string
  password: boolean
  type: 'string' | 'stringList' | 'integer' | 'boolean'
  value: string
  deprecated: string
}
type Hint = { id: string; value: string }

type Meta = { title: string; text: string; icon: React.ReactNode }

const groups: { title: string; ids: string[] }[] = [
  { title: 'Basics', ids: ['General', 'Services', 'Maintenance'] },
  { title: 'Sign-in', ids: ['Auth', 'OIDC', 'GitHub'] },
  {
    title: 'Notifications',
    ids: ['Twilio', 'SMTP', 'Mailgun', 'Slack', 'Webhook'],
  },
  { title: 'Other', ids: ['Feedback'] },
]

const meta: Record<string, Meta> = {
  General: {
    title: 'General',
    text: 'Name, links and what people see in messages.',
    icon: <Tune />,
  },
  Services: {
    title: 'Services',
    text: 'Rules every service has to follow.',
    icon: <Dns />,
  },
  Maintenance: {
    title: 'Cleanup',
    text: 'How long alerts, schedules and API keys are kept.',
    icon: <CleaningServices />,
  },
  Auth: {
    title: 'Sign-in',
    text: 'How people sign in to On-Call.',
    icon: <Lock />,
  },
  OIDC: {
    title: 'Single sign-on',
    text: 'Sign in with Microsoft Entra or another OpenID Connect provider.',
    icon: <Login />,
  },
  GitHub: {
    title: 'GitHub',
    text: 'Sign in with GitHub accounts.',
    icon: <GitHub />,
  },
  Twilio: {
    title: 'Twilio',
    text: 'SMS and phone calls.',
    icon: <Sms />,
  },
  SMTP: {
    title: 'Email',
    text: 'Email notifications through an SMTP server.',
    icon: <Email />,
  },
  Mailgun: {
    title: 'Mailgun',
    text: 'Alerts created from incoming email.',
    icon: <MarkEmailRead />,
  },
  Slack: {
    title: 'Slack',
    text: 'Alerts in Slack channels and direct messages.',
    icon: <Forum />,
  },
  Webhook: {
    title: 'Webhooks',
    text: 'Alerts sent to other systems over HTTP.',
    icon: <Webhook />,
  },
  Feedback: {
    title: 'Feedback',
    text: 'The feedback link in the menu.',
    icon: <Feedback />,
  },
}

const metaOf = (s: string): Meta =>
  meta[s] ?? { title: startCase(s), text: '', icon: <Settings /> }
const sectionOf = (id: string): string => id.split('.')[0]
const keyOf = (id: string): string => id.split('.').slice(1).join('.')

// labelOf: "Twilio.DisableTwoWaySMS" -> "Disable two-way SMS"
function labelOf(id: string): string {
  const words = startCase(keyOf(id))
    .replace(/\bTwo Way\b/, 'Two-Way')
    .replace('Enable V 1 Graph QL', 'Enable V1 GraphQL')
    .replace('Git Hub', 'GitHub')
    .replace(/R Ls\b/, 'RLs')
    .split(' ')
  return words
    .map((w, i) =>
      i === 0 || /^[A-Z0-9]{2,}s?$/.test(w) || w === 'GitHub'
        ? w
        : w.toLowerCase(),
    )
    .join(' ')
}

// shown in the review dialog
function display(f: ConfigValue, v: string): string {
  if (f.password) return v ? '••••••••' : 'not set'
  if (f.type === 'boolean') return v === 'true' ? 'On' : 'Off'
  if (f.type === 'stringList') return v ? v.split('\n').join(', ') : 'none'
  return v === '' ? 'empty' : v
}

function copy(text: string): void {
  navigator.clipboard?.writeText(text).catch(() => {})
}

function PasswordInput(props: {
  value: string
  onChange: (v: string) => void
}): React.JSX.Element {
  const [show, setShow] = useState(false)
  return (
    <TextField
      size='small'
      fullWidth
      autoComplete='new-password' // stops the browser filling saved passwords in
      type={show ? 'text' : 'password'}
      value={props.value}
      placeholder='Not set'
      onChange={(e) => props.onChange(e.target.value)}
      InputProps={{
        endAdornment: (
          <InputAdornment position='end'>
            <IconButton
              size='small'
              aria-label={show ? 'Hide' : 'Show'}
              onClick={() => setShow(!show)}
            >
              {show ? (
                <VisibilityOff fontSize='small' />
              ) : (
                <Visibility fontSize='small' />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}

function ListInput(props: {
  value: string
  onChange: (v: string) => void
}): React.JSX.Element {
  const items = props.value ? props.value.split('\n') : []
  const [draft, setDraft] = useState('')
  const set = (next: string[]): void =>
    props.onChange(next.filter((v) => v.trim()).join('\n'))
  const add = (): void => {
    if (!draft.trim()) return
    set([...items, draft.trim()])
    setDraft('')
  }
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {items.map((v, i) => (
        <TextField
          key={i}
          size='small'
          fullWidth
          value={v}
          onChange={(e) =>
            set(items.map((x, k) => (k === i ? e.target.value : x)))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  size='small'
                  aria-label='Remove'
                  onClick={() => set(items.filter((_, k) => k !== i))}
                >
                  <Close fontSize='small' />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      ))}
      <TextField
        size='small'
        fullWidth
        value={draft}
        placeholder={items.length ? 'Add another' : 'Add one'}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          }
        }}
        onBlur={add}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                size='small'
                aria-label='Add'
                disabled={!draft.trim()}
                onClick={add}
              >
                <Add fontSize='small' />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  )
}

function Input(props: {
  f: ConfigValue
  value: string
  onChange: (v: string) => void
}): React.JSX.Element {
  const { f, value, onChange } = props
  if (f.type === 'boolean')
    return (
      <Switch
        checked={value === 'true'}
        onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        inputProps={{ 'aria-label': labelOf(f.id) }}
      />
    )
  if (f.type === 'stringList')
    return <ListInput value={value} onChange={onChange} />
  if (f.password) return <PasswordInput value={value} onChange={onChange} />
  return (
    <TextField
      size='small'
      fullWidth
      type={f.type === 'integer' ? 'number' : 'text'}
      inputProps={f.type === 'integer' ? { min: 0, max: 9000 } : undefined}
      value={value}
      placeholder={f.type === 'integer' ? '0' : 'Not set'}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

// Linked turns the URLs in a description into links.
function Linked(props: { text: string }): React.JSX.Element {
  const c = useBrand()
  return (
    <React.Fragment>
      {props.text.split(/(https?:\/\/[^\s)]+[^\s).,])/).map((part, i) =>
        i % 2 ? (
          <Link
            key={i}
            href={part}
            target='_blank'
            rel='noopener noreferrer'
            sx={{ color: c.ink, wordBreak: 'break-all' }}
          >
            {part.replace(/^https?:\/\//, '')}
          </Link>
        ) : (
          part
        ),
      )}
    </React.Fragment>
  )
}

function Field(props: {
  f: ConfigValue
  value: string
  changed: boolean
  inSection?: string // set when listed outside its section (search results)
  onChange: (v: string) => void
  onUndo: () => void
}): React.JSX.Element {
  const c = useBrand()
  const { f } = props
  const toggle = f.type === 'boolean'
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        // switches sit on the right; other inputs go under the description
        gridTemplateColumns: toggle ? 'minmax(0, 1fr) auto' : '1fr',
        columnGap: 3,
        rowGap: 1,
        alignItems: 'start',
        px: 2.5,
        py: 1.75,
        '& + &': { borderTop: `1px solid ${c.line}` },
      }}
    >
      {props.changed && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 10,
            bottom: 10,
            width: 4,
            borderRadius: '0 4px 4px 0',
            bgcolor: c.brand,
          }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: c.ink }}>
            {props.inSection && (
              <Box component='span' sx={{ color: c.muted, fontWeight: 400 }}>
                {props.inSection} ›{' '}
              </Box>
            )}
            {labelOf(f.id)}
          </Typography>
          {f.deprecated && (
            <Chip size='small' variant='outlined' label='Deprecated' />
          )}
          {props.changed && (
            <Tooltip title='Undo this change'>
              <IconButton
                size='small'
                aria-label='Undo this change'
                onClick={props.onUndo}
                sx={{ ml: -0.5 }}
              >
                <Undo sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {f.description && (
          <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
            <Linked text={f.description} />
          </Typography>
        )}
        {f.deprecated && (
          <Typography sx={{ fontSize: 13, color: c.warn, mt: 0.25 }}>
            {f.deprecated}
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0, maxWidth: toggle ? undefined : 520 }}>
        <Input f={f} value={props.value} onChange={props.onChange} />
      </Box>
    </Box>
  )
}

function Hints(props: { hints: Hint[] }): React.JSX.Element | null {
  const c = useBrand()
  const [copied, setCopied] = useState('')
  if (!props.hints.length) return null
  return (
    <Box
      sx={{
        mx: 2.5,
        my: 2,
        p: 1.5,
        borderRadius: '12px',
        bgcolor: c.bg,
        border: `1px solid ${c.line}`,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.ink, mb: 1 }}>
        Give these to the provider
      </Typography>
      {props.hints.map((h) => (
        <Box
          key={h.id}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: c.muted }}>
              {labelOf(h.id)}
            </Typography>
            <Typography
              sx={{ fontSize: 13, fontFamily: 'monospace', color: c.ink }}
              noWrap
              title={h.value}
            >
              {h.value}
            </Typography>
          </Box>
          <Tooltip title={copied === h.id ? 'Copied' : 'Copy'}>
            <IconButton
              size='small'
              aria-label={`Copy ${labelOf(h.id)}`}
              onClick={() => {
                copy(h.value)
                setCopied(h.id)
              }}
            >
              <ContentCopy fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  )
}

function NavItem(props: {
  id: string
  active: boolean
  enabled?: boolean // undefined: the section has no on/off switch
  changes: number
  matches?: number // while searching
  onClick: () => void
}): React.JSX.Element {
  const c = useBrand()
  const m = metaOf(props.id)
  const dim = props.matches === 0
  // on phones the list is a sideways row: keep the open section in view
  const ref = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (props.active)
      ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [props.active])
  return (
    <Box
      ref={ref}
      component='button'
      type='button'
      onClick={props.onClick}
      aria-current={props.active ? 'page' : undefined}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        flex: { xs: '0 0 auto', md: 'initial' },
        px: 1.25,
        py: 0.9,
        borderRadius: '10px',
        color: c.ink,
        opacity: dim ? 0.45 : 1,
        bgcolor: props.active ? c.soft : 'transparent',
        border: {
          xs: `1px solid ${props.active ? c.ink : c.line}`,
          md: 'none',
        },
        '&:hover': { bgcolor: props.active ? c.soft : c.hover },
        '&:focus-visible': { outline: `2px solid ${c.brand}` },
        '& svg': { fontSize: 19, color: props.active ? c.ink : c.muted },
      }}
    >
      {m.icon}
      <Typography
        sx={{
          flex: 1,
          fontSize: 14,
          fontWeight: props.active ? 600 : 500,
          whiteSpace: 'nowrap',
        }}
      >
        {m.title}
      </Typography>
      {props.matches !== undefined && props.matches > 0 && (
        <Typography sx={{ fontSize: 12, color: c.muted }}>
          {props.matches}
        </Typography>
      )}
      {props.changes > 0 && (
        <Box
          title={`${props.changes} unsaved`}
          sx={{
            minWidth: 18,
            height: 18,
            px: 0.5,
            borderRadius: 9,
            bgcolor: c.brand,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {props.changes}
        </Box>
      )}
      {props.enabled !== undefined && props.changes === 0 && (
        <Box
          title={props.enabled ? 'On' : 'Off'}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: props.enabled ? c.ok : 'transparent',
            border: `1.5px solid ${props.enabled ? c.ok : c.muted}`,
          }}
        />
      )}
    </Box>
  )
}

export default function ConfigPage(): React.JSX.Element {
  const c = useBrand()
  const [q, refetch] = useQuery({ query: configQuery, context: noSuspense })
  const [saving, commit] = useMutation(setConfig)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [section, setSection] = useURLParam<string>('section', 'General')
  const [search, setSearch] = useState('')
  const [review, setReview] = useState(false)
  const [saved, setSaved] = useState(false)

  const fields: ConfigValue[] = q.data?.config ?? []
  const hints: Hint[] = q.data?.configHints ?? []
  const byID = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.id, f])),
    [fields],
  )

  // every section the server knows, in our grouping; unknown ones go last
  const nav = useMemo(() => {
    const known = new Set(groups.flatMap((g) => g.ids))
    const present = new Set(fields.map((f) => sectionOf(f.id)))
    const extra = Array.from(present).filter((s) => !known.has(s))
    return [
      ...groups.map((g) => ({
        title: g.title,
        ids: g.ids.filter((s) => present.has(s)),
      })),
      { title: 'More', ids: extra },
    ].filter((g) => g.ids.length)
  }, [fields])

  if (q.error) return <GenericError error={q.error.message} />

  const valueOf = (id: string): string => edits[id] ?? byID[id]?.value ?? ''
  const change = (id: string, v: string): void => {
    setSaved(false)
    const next = { ...edits }
    if (v === byID[id]?.value) delete next[id]
    else next[id] = v
    setEdits(next)
  }
  const undo = (id: string): void => change(id, byID[id]?.value ?? '')
  const changed = Object.keys(edits)
  const changesIn = (s: string): number =>
    changed.filter((id) => sectionOf(id) === s).length
  const enabledOf = (s: string): boolean | undefined =>
    byID[s + '.Enable'] ? valueOf(s + '.Enable') === 'true' : undefined

  const term = search.trim().toLowerCase()
  const matches = (f: ConfigValue): boolean =>
    !term ||
    [f.id, labelOf(f.id), metaOf(sectionOf(f.id)).title, f.description]
      .join(' ')
      .toLowerCase()
      .includes(term)
  const found = term ? fields.filter(matches) : []

  const current = fields.some((f) => sectionOf(f.id) === section)
    ? section
    : 'General'
  const m = metaOf(current)
  const enabled = enabledOf(current)
  const sectionFields = fields.filter(
    (f) => sectionOf(f.id) === current && keyOf(f.id) !== 'Enable',
  )
  const live = sectionFields.filter((f) => !f.deprecated)
  const old = sectionFields.filter((f) => f.deprecated)

  const renderField = (
    f: ConfigValue,
    inSection?: string,
  ): React.JSX.Element => (
    <Field
      key={f.id}
      f={f}
      value={valueOf(f.id)}
      changed={f.id in edits}
      inSection={inSection}
      onChange={(v) => change(f.id, v)}
      onUndo={() => undo(f.id)}
    />
  )

  // errorsOf names the settings that failed validation ("Twilio › Account
  // SID: must not be empty") instead of "Multiple fields failed validation."
  const errorsOf = (err: CombinedError): string[] => {
    // "X.Enable: requires X.Y to be set" repeats "X.Y: required to enable X"
    const fe = fieldErrors(err)
    const own = fe.filter((e) => !/\.enable$/i.test(e.field))
    const named = (own.length ? own : fe).map((e) => {
      const id = fields.find(
        (f) => f.id.toLowerCase() === e.field.toLowerCase(),
      )?.id
      return id
        ? `${metaOf(sectionOf(id)).title} › ${labelOf(id)}: ${e.message}`
        : `${e.field}: ${e.message}`
    })
    const other = nonFieldErrors(err).map((e) =>
      e.message.replace(/^\[GraphQL\] /, ''),
    )
    return named.length ? named : other
  }

  const save = (): void => {
    commit(
      { input: changed.map((id) => ({ id, value: edits[id] })) },
      { additionalTypenames: ['ConfigValue'] },
    ).then((r) => {
      if (r.error) return
      setEdits({})
      setReview(false)
      setSaved(true)
      refetch({ requestPolicy: 'network-only' })
    })
  }

  return (
    <Page
      title='Configuration'
      subtitle='Settings for this On-Call instance. Saved changes apply to everyone right away.'
      actions={
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder='Search settings'
        />
      }
      maxWidth={1160}
    >
      {!q.data && <Typography sx={{ color: c.muted }}>Loading…</Typography>}
      {q.data && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '230px minmax(0, 1fr)' },
            gap: { xs: 2, md: 3 },
            alignItems: 'start',
          }}
        >
          <Box
            component='nav'
            aria-label='Configuration sections'
            sx={{
              position: { md: 'sticky' },
              top: { md: 16 },
              display: 'flex',
              flexDirection: { xs: 'row', md: 'column' },
              gap: { xs: 1, md: 0.25 },
              overflowX: { xs: 'auto', md: 'visible' },
              pb: { xs: 0.5, md: 0 },
            }}
          >
            {nav.map((g) => (
              <React.Fragment key={g.title}>
                <Typography
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: c.muted,
                    px: 1.25,
                    pt: 1.5,
                    pb: 0.5,
                  }}
                >
                  {g.title}
                </Typography>
                {g.ids.map((s) => (
                  <NavItem
                    key={s}
                    id={s}
                    active={!term && s === current}
                    enabled={enabledOf(s)}
                    changes={changesIn(s)}
                    matches={
                      term
                        ? found.filter((f) => sectionOf(f.id) === s).length
                        : undefined
                    }
                    onClick={() => {
                      setSearch('')
                      setSection(s)
                    }}
                  />
                ))}
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
            {saved && (
              <Alert severity='success' onClose={() => setSaved(false)}>
                Saved.
              </Alert>
            )}

            {term ? (
              <Panel
                title={`${found.length} setting${found.length === 1 ? '' : 's'} match “${search.trim()}”`}
                flush
              >
                {found.length === 0 && (
                  <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
                    Nothing matches. Try another word, like “SMS” or “sign”.
                  </Typography>
                )}
                {found.map((f) =>
                  renderField(f, metaOf(sectionOf(f.id)).title),
                )}
              </Panel>
            ) : (
              <Card sx={{ overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 2,
                    borderBottom: `1px solid ${c.line}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      bgcolor: c.soft,
                      border: `1px solid ${c.line}`,
                      color: c.ink,
                    }}
                  >
                    {m.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      component='h2'
                      sx={{ fontSize: 18, fontWeight: 600, color: c.ink }}
                    >
                      {m.title}
                    </Typography>
                    {m.text && (
                      <Typography sx={{ fontSize: 13, color: c.muted }}>
                        {m.text}
                      </Typography>
                    )}
                  </Box>
                  {enabled !== undefined && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: enabled ? c.ok : c.muted,
                        }}
                      >
                        {enabled ? 'On' : 'Off'}
                      </Typography>
                      <Switch
                        checked={enabled}
                        onChange={(e) =>
                          change(
                            current + '.Enable',
                            e.target.checked ? 'true' : 'false',
                          )
                        }
                        inputProps={{ 'aria-label': `Turn ${m.title} on` }}
                      />
                    </Box>
                  )}
                </Box>
                {enabled === false && (
                  <Typography
                    sx={{
                      px: 2.5,
                      py: 1.25,
                      fontSize: 13,
                      color: c.muted,
                      bgcolor: c.bg,
                      borderBottom: `1px solid ${c.line}`,
                    }}
                  >
                    {m.title} is off. The settings below are kept, and used once
                    you turn it on.
                  </Typography>
                )}
                <Box sx={{ opacity: enabled === false ? 0.75 : 1 }}>
                  {live.map((f) => renderField(f))}
                </Box>
                <Hints
                  hints={hints.filter((h) => sectionOf(h.id) === current)}
                />
                {current === 'Slack' && (
                  <Box sx={{ px: 1, pb: 1 }}>
                    <SlackActions />
                  </Box>
                )}
                {old.length > 0 && (
                  <Box sx={{ borderTop: `1px solid ${c.line}` }}>
                    <Typography
                      sx={{
                        px: 2.5,
                        pt: 1.5,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: c.muted,
                      }}
                    >
                      Deprecated
                    </Typography>
                    {old.map((f) => renderField(f))}
                  </Box>
                )}
              </Card>
            )}
          </Box>
        </Box>
      )}

      {changed.length > 0 && (
        <Card
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 3,
            px: 2.5,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            border: `1px solid ${c.line}`,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
            zIndex: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: 600, color: c.ink }}>
              {changed.length} unsaved change{changed.length === 1 ? '' : 's'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: c.muted }} noWrap>
              {Array.from(new Set(changed.map(sectionOf)))
                .map((s) => metaOf(s).title)
                .join(', ')}
            </Typography>
          </Box>
          <Button onClick={() => setEdits({})}>Discard</Button>
          <Button variant='contained' onClick={() => setReview(true)}>
            Review and save
          </Button>
        </Card>
      )}

      <Dialog
        open={review}
        onClose={() => !saving.fetching && setReview(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>
          Save {changed.length} change{changed.length === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: c.muted, mb: 1.5 }}>
            They apply to everyone as soon as you save.
          </Typography>
          {changed
            .slice()
            .sort()
            .map((id) => {
              const f = byID[id]
              if (!f) return null
              return (
                <Box
                  key={id}
                  sx={{
                    py: 1.25,
                    '& + &': { borderTop: `1px solid ${c.line}` },
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: c.muted }}>
                    {metaOf(sectionOf(id)).title}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: c.ink }}>
                    {keyOf(id) === 'Enable'
                      ? `Turn ${metaOf(sectionOf(id)).title} ${edits[id] === 'true' ? 'on' : 'off'}`
                      : labelOf(id)}
                  </Typography>
                  {keyOf(id) !== 'Enable' && (
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: c.ink,
                        wordBreak: 'break-word',
                      }}
                    >
                      <Box
                        component='span'
                        sx={{ color: c.muted, textDecoration: 'line-through' }}
                      >
                        {display(f, f.value)}
                      </Box>
                      {' → '}
                      {f.password && edits[id]
                        ? 'new value'
                        : display(f, edits[id])}
                    </Typography>
                  )}
                </Box>
              )
            })}
          {saving.error && (
            <Alert severity='error' sx={{ mt: 1.5 }}>
              {errorsOf(saving.error).map((e, i) => (
                <Box key={i}>{e}</Box>
              ))}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview(false)} disabled={saving.fetching}>
            Back
          </Button>
          <Button variant='contained' onClick={save} disabled={saving.fetching}>
            {saving.fetching ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  )
}
